import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { getAllOptions, useCatalog } from '@/api/catalog';
import { api, ApiError } from '@/api/client';
import { Icon } from '@/components/Icon';
import { money, qty } from '@/lib/format';
import { createPurchaseOrder, createPurchaseOrderDraft, editPurchaseOrderDraft } from './api';
import type { PurchaseOrderDetail, SupplierOption, Warehouse } from './types';
import { PurchaseCloseGuard } from './PurchaseCloseGuard';
import s from './PurchaseCreateDialog.module.css';

type Quote = { id: string; supplierId: string; quotePrice: number | string; isDefault?: boolean };
type Option = { id: string; packageName?: string | null; packageMethod?: string | null; description?: string | null; packageUnit?: string | null; unitsPerPackage?: number | string | null; supplierQuotes?: Quote[] };
type Product = { id: string; code: string; name: string; category?: string | null; packagingOptions?: Option[] };
type DraftLine = { key: string; product: Product; optionId: string; quoteId?: string; packageQuantity?: number; quantity: number; unitCost: number; note: string };

function optionLabel(option: Option, index: number) {
  return option.packageName || option.packageMethod || option.description || `包装方案 ${index + 1}`;
}

function supplierQuote(option: Option | undefined, supplierId: string) {
  const quotes = option?.supplierQuotes?.filter((item) => item.supplierId === supplierId) || [];
  return quotes.find((item) => item.isDefault) || quotes[0];
}

export function PurchaseCreateDialog({ suppliers: _initialSuppliers, warehouses: initialWarehouses, initialOrder, onClose, onCreated }: {
  suppliers: SupplierOption[];
  warehouses: Warehouse[];
  initialOrder?: PurchaseOrderDetail;
  onClose: () => void;
  onCreated: () => void;
}) {
  const warehouseCatalog = useCatalog<Warehouse>('/api/warehouses');
  const warehouses = warehouseCatalog.data;
  const supplierCatalog = useCatalog<SupplierOption>('/api/suppliers?scope=purchase-options');
  const suppliers = supplierCatalog.data;
  const optionsError = warehouseCatalog.error || supplierCatalog.error;
  const optionsLoading = warehouseCatalog.loading || supplierCatalog.loading;
  const retryOptions = () => { warehouseCatalog.reload(); supplierCatalog.reload(); };
  const [supplierId, setSupplierId] = useState(initialOrder?.supplier.id || '');
  const [warehouseId, setWarehouseId] = useState(() => initialOrder?.warehouse.id || initialWarehouses.find((item) => item.isDefaultInbound)?.id || initialWarehouses[0]?.id || '');
  const [orderDate, setOrderDate] = useState(() => {
    if (initialOrder?.orderDate) return initialOrder.orderDate.slice(0, 10);
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  });
  const [arrivalDate, setArrivalDate] = useState(initialOrder?.expectedArrivalDate?.slice(0, 10) || '');
  const [note, setNote] = useState(initialOrder?.note || '');
  const [lines, setLines] = useState<DraftLine[]>(() => initialOrder?.lines.map((line) => ({
    key: line.id,
    product: {
      ...line.product,
      packagingOptions: line.packagingOption ? [{
        ...line.packagingOption,
        supplierQuotes: line.supplierQuote ? [{ ...line.supplierQuote, supplierId: initialOrder.supplier.id }] : [],
      }] : [],
    },
    optionId: line.packagingOption?.id || '',
    quoteId: line.supplierQuote?.id,
    packageQuantity: line.packageQuantity == null ? undefined : Number(line.packageQuantity),
    quantity: Number(line.quantity),
    unitCost: Number(line.unitCost || 0),
    note: line.note || '',
  })) || []);
  const [editOptionsError, setEditOptionsError] = useState('');
  const [editOptionsLoading, setEditOptionsLoading] = useState(Boolean(initialOrder));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [searchRevision, setSearchRevision] = useState(0);
  const [submittedKeyword, setSubmittedKeyword] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [productLoading, setProductLoading] = useState(false);
  const [productError, setProductError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState<{ id: string; purchaseNo: string; draft: boolean } | null>(null);
  const [closeGuardOpen, setCloseGuardOpen] = useState(false);
  const currentSnapshot = JSON.stringify({ supplierId, warehouseId, orderDate, arrivalDate, note, lines: lines.map((line) => ({ productId: line.product.id, optionId: line.optionId, quoteId: line.quoteId, packageQuantity: line.packageQuantity, quantity: line.quantity, unitCost: line.unitCost, note: line.note })) });
  const initialSnapshot = useRef(currentSnapshot);
  const dirty = currentSnapshot !== initialSnapshot.current;

  function requestClose() {
    if (saving) return;
    if (created || !dirty) onClose();
    else setCloseGuardOpen(true);
  }

  useEffect(() => { if (!warehouseCatalog.loading && !warehouseCatalog.error) setWarehouseId((current) => current || warehouses.find((item) => item.isDefaultInbound)?.id || warehouses[0]?.id || ''); }, [warehouses, warehouseCatalog.loading, warehouseCatalog.error]);

  useEffect(() => {
    if (!initialOrder) return;
    const controller = new AbortController();
    void api.get<Product[]>(`/api/v2/purchase-orders/${encodeURIComponent(initialOrder.id)}/edit-options`, undefined, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        const byId = new Map(result.map((product) => [product.id, product]));
        setLines((current) => current.map((line) => {
          const product = byId.get(line.product.id);
          return product ? { ...line, product } : line;
        }));
        setEditOptionsError('');
      })
      .catch((cause: Error) => { if (!controller.signal.aborted) setEditOptionsError(cause.message); })
      .finally(() => { if (!controller.signal.aborted) setEditOptionsLoading(false); });
    return () => controller.abort();
  }, [initialOrder]);

  useEffect(() => {
    if (!pickerOpen) return;
    const controller = new AbortController();
    setProductLoading(true);
    setProductError('');
    void getAllOptions<Product>('/api/products', { scope: 'purchase-options', keyword: submittedKeyword || undefined }, controller.signal)
      .then((result) => { if (!controller.signal.aborted) setProducts(result); })
      .catch((cause: Error) => { if (!controller.signal.aborted) setProductError(cause.message); })
      .finally(() => { if (!controller.signal.aborted) setProductLoading(false); });
    return () => controller.abort();
  }, [pickerOpen, submittedKeyword, searchRevision]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !saving) {
        if (closeGuardOpen) setCloseGuardOpen(false);
        else if (pickerOpen) setPickerOpen(false);
        else requestClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, pickerOpen, saving, closeGuardOpen, dirty, created]);

  const total = useMemo(() => lines.reduce((sum, line) => sum + line.quantity * line.unitCost, 0), [lines]);
  const invalid = optionsLoading || !!optionsError || !supplierId || !warehouseId || !lines.length || editOptionsLoading || lines.some((line) => !line.optionId || !Number.isFinite(line.quantity) || line.quantity <= 0 || !Number.isFinite(line.unitCost) || line.unitCost < 0);

  function addProduct(product: Product) {
    const option = product.packagingOptions?.find((item) => supplierQuote(item, supplierId)) || product.packagingOptions?.[0];
    if (!option) return;
    const quote = supplierQuote(option, supplierId);
    setLines((current) => [...current, {
      key: `${product.id}-${Date.now()}-${current.length}`,
      product,
      optionId: option.id,
      quoteId: quote?.id,
      quantity: 1,
      unitCost: Number(quote?.quotePrice || 0),
      note: '',
    }]);
    setPickerOpen(false);
  }

  function changeLine(key: string, patch: Partial<DraftLine>) {
    setLines((current) => current.map((line) => {
      if (line.key !== key) return line;
      const next = { ...line, ...patch };
      if (patch.optionId) {
        const option = line.product.packagingOptions?.find((item) => item.id === patch.optionId);
        const quote = supplierQuote(option, supplierId);
        next.quoteId = quote?.id;
        next.unitCost = Number(quote?.quotePrice || 0);
        next.packageQuantity = undefined;
      }
      if (patch.packageQuantity !== undefined) {
        const option = line.product.packagingOptions?.find((item) => item.id === next.optionId);
        const units = Number(option?.unitsPerPackage || 0);
        if (units > 0 && patch.packageQuantity > 0) next.quantity = Number((patch.packageQuantity * units).toFixed(3));
        else next.packageQuantity = undefined;
      }
      if (patch.quantity !== undefined) next.packageQuantity = undefined;
      return next;
    }));
  }

  async function submit(draft: boolean, closeAfter = false) {
    if (saving || uncertain || invalid) return;
    setSaving(true);
    setSavingDraft(draft);
    setError('');
    try {
      const payload = {
        supplierId,
        warehouseId,
        orderDate: orderDate || undefined,
        expectedArrivalDate: arrivalDate || undefined,
        note: note.trim() || undefined,
        lines: lines.map((line) => ({
          productId: line.product.id,
          packagingOptionId: line.optionId,
          supplierQuoteId: line.quoteId,
          packageQuantity: line.packageQuantity,
          quantity: line.quantity,
          unitCost: line.unitCost,
          note: line.note.trim() || undefined,
        })),
      };
      const result = initialOrder
        ? await editPurchaseOrderDraft(initialOrder.id, { ...payload, expectedUpdatedAt: initialOrder.updatedAt })
        : draft ? await createPurchaseOrderDraft(payload) : await createPurchaseOrder(payload);
      setCreated({ ...result, draft: Boolean(initialOrder || draft) });
      onCreated();
      if (closeAfter) onClose();
    } catch (cause) {
      if (!(cause instanceof ApiError) || cause.status >= 500) setUncertain(true);
      setError(cause instanceof Error ? cause.message : '创建失败');
    } finally {
      setSaving(false);
    }
  }

  return <div className={s.backdrop} onMouseDown={(event) => { if (event.target === event.currentTarget) requestClose(); }}>
    <section className={s.dialog} role="dialog" aria-modal="true" aria-label={initialOrder ? '编辑采购草稿' : '新建采购单'}>
      <header className={s.head}><h2>{initialOrder ? `编辑采购草稿 · ${initialOrder.purchaseNo}` : '新建采购单'}</h2><button type="button" onClick={requestClose} disabled={saving} aria-label="关闭"><Icon name="x" size={18} /></button></header>
      {created ? <div className={s.success}><Icon name="check" size={24} /><h3>{initialOrder ? '采购草稿修改已保存' : created.draft ? '采购单草稿已保存' : '采购单已创建'}</h3><Link to={`/purchase-orders/${created.id}`} onClick={onClose}>{created.purchaseNo}</Link><button type="button" onClick={onClose}>{initialOrder ? '返回采购单详情' : '返回采购单列表'}</button></div> :
        <form onSubmit={(event) => { event.preventDefault(); void submit(false); }}>
          <div className={s.body}>{optionsError && <p className={s.error} role="alert">资料加载失败：{optionsError.message} <button type="button" onClick={retryOptions}>重试</button></p>}
            <div className={s.info}>
              <label>供应商 <em>*</em><select required value={supplierId} onChange={(event) => { const nextSupplierId = event.target.value; setSupplierId(nextSupplierId); setLines((current) => current.map((line) => {
                const currentOption = line.product.packagingOptions?.find((item) => item.id === line.optionId);
                const option = (supplierQuote(currentOption, nextSupplierId) ? currentOption : line.product.packagingOptions?.find((item) => supplierQuote(item, nextSupplierId))) || currentOption;
                const quote = supplierQuote(option, nextSupplierId);
                return { ...line, optionId: option?.id || line.optionId, quoteId: quote?.id, unitCost: quote ? Number(quote.quotePrice) : line.unitCost, packageQuantity: option?.id === line.optionId ? line.packageQuantity : undefined };
              })); }}><option value="">请选择供应商</option>{initialOrder && !suppliers.some((item) => item.id === initialOrder.supplier.id) && <option value={initialOrder.supplier.id}>{initialOrder.supplier.name}（原单供应商）</option>}{suppliers.filter((item) => item.isActive !== false).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
              <label>入库仓库 <em>*</em><select required value={warehouseId} onChange={(event) => setWarehouseId(event.target.value)}><option value="">请选择仓库</option>{initialOrder && !warehouses.some((item) => item.id === initialOrder.warehouse.id) && <option value={initialOrder.warehouse.id}>{initialOrder.warehouse.name}（原单仓库）</option>}{warehouses.map((item) => <option key={item.id} value={item.id}>{item.code} · {item.name}</option>)}</select></label>
              <label>预计到货<input type="date" value={arrivalDate} min={orderDate || undefined} onChange={(event) => setArrivalDate(event.target.value)} /></label>
              <label>采购日期<input type="date" value={orderDate} onChange={(event) => setOrderDate(event.target.value)} /></label>
            </div>
            {lines.length > 0 && <p className={s.hint}>更换供应商保留产品和采购量，并带入已有包装报价；无报价的明细保留单价，请核对。</p>}
            {editOptionsError && <p className={s.error} role="alert">包装方案加载失败：{editOptionsError}。当前明细保留原包装方案；如需更换，请关闭后重试。</p>}
            <div className={s.sectionHead}><h3>采购明细 <small>{lines.length} 项 · {money(total)}</small></h3><button type="button" onClick={() => { setKeyword(''); setSubmittedKeyword(''); setPickerOpen(true); }} disabled={!supplierId}><Icon name="plus" size={14} />添加产品</button></div>
            {lines.length ? <div className={s.tableWrap}><table className={s.table}><thead><tr><th>产品</th><th>包装方案</th><th>包装件数</th><th>采购量</th><th>单价</th><th>金额</th><th>操作</th></tr></thead><tbody>{lines.map((line) => {
              const options = line.product.packagingOptions || [];
              const option = options.find((item) => item.id === line.optionId);
              const units = Number(option?.unitsPerPackage || 0);
              return <tr key={line.key}>
                <td><b>{line.product.name}</b><small className="tnum">{line.product.code}</small></td>
                <td><select aria-label={`${line.product.name}包装方案`} value={line.optionId} onChange={(event) => changeLine(line.key, { optionId: event.target.value })}>{options.map((item, index) => <option value={item.id} key={item.id}>{optionLabel(item, index)}</option>)}</select><small>{units > 0 ? `每${option?.packageUnit || '件'} ${qty(units)} 个` : '按数量采购'}</small></td>
                <td><input type="number" aria-label={`${line.product.name}包装件数`} min="1" step="1" value={line.packageQuantity ?? ''} disabled={units <= 0} onChange={(event) => changeLine(line.key, { packageQuantity: Number(event.target.value) || undefined })} /></td>
                <td><input type="number" aria-label={`${line.product.name}采购量`} min="0.001" step="0.001" required value={line.quantity} onChange={(event) => changeLine(line.key, { quantity: Number(event.target.value) })} /></td>
                <td><input type="number" aria-label={`${line.product.name}单价`} min="0" step="0.01" required value={line.unitCost} onChange={(event) => changeLine(line.key, { unitCost: Number(event.target.value) })} /></td>
                <td className="tnum">{money(line.quantity * line.unitCost)}</td>
                <td><button type="button" className={s.remove} aria-label={`删除${line.product.name}`} onClick={() => setLines((current) => current.filter((item) => item.key !== line.key))}><Icon name="trash" size={16} /></button></td>
              </tr>;
            })}</tbody></table></div> : <p className={s.empty}>请先选择供应商，再添加产品和包装方案。</p>}
            <label className={s.note}>备注<textarea rows={2} value={note} onChange={(event) => setNote(event.target.value)} /></label>
            {error && <p className={s.error} role="alert">{error}{uncertain && '。请求结果未确认，请先核对采购单列表，暂勿重复提交。'}</p>}
          </div>
          <footer className={s.foot}><span>合计 <b>{money(total)}</b></span><button type="button" onClick={requestClose} disabled={saving}>取消</button>{!initialOrder && <button type="button" disabled={saving || uncertain || invalid} onClick={() => void submit(true)}>{savingDraft && saving ? '正在保存…' : '存为草稿'}</button>}<button type="submit" className={s.submit} disabled={saving || uncertain || invalid}>{saving ? '正在保存…' : initialOrder ? '保存修改' : '确认创建'}</button></footer>
        </form>}
      {pickerOpen && <div className={s.pickerBackdrop} onMouseDown={(event) => { if (event.target === event.currentTarget) setPickerOpen(false); }}>
        <section className={s.picker} role="dialog" aria-modal="true" aria-label="选择产品">
          <header className={s.head}><h2>选择产品</h2><button type="button" onClick={() => setPickerOpen(false)} aria-label="关闭"><Icon name="x" size={18} /></button></header>
          <div className={s.pickerSearch}><input autoFocus placeholder="搜索产品编号、名称、分类" value={keyword} onChange={(event) => setKeyword(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); setSubmittedKeyword(keyword.trim()); setSearchRevision((value) => value + 1); } }} /><button type="button" onClick={() => { setSubmittedKeyword(keyword.trim()); setSearchRevision((value) => value + 1); }}>搜索</button></div>
          <div className={s.pickerList}>{productLoading ? <p>正在加载产品…</p> : productError ? <p className={s.error}>{productError}</p> : products.length ? products.map((product) => <div className={s.product} key={product.id}><div><b>{product.name}</b><small>{product.code}{product.category ? ` · ${product.category}` : ''}</small><small>{product.packagingOptions?.length || 0} 个包装方案</small></div><button type="button" disabled={!product.packagingOptions?.length} onClick={() => addProduct(product)}>{product.packagingOptions?.length ? '添加' : '未设置包装'}</button></div>) : <p>没有符合条件的产品。</p>}</div>
        </section>
      </div>}
      {closeGuardOpen && <PurchaseCloseGuard detail={`${lines.length} 项采购明细，金额 ${money(total)}。放弃后，本次修改将丢失。`} error={error ? `${error}${uncertain ? '。请求结果未确认，请先核对采购单列表，暂勿重复提交。' : ''}` : undefined} saveDisabled={invalid || uncertain} saving={saving} onSave={() => void submit(true, true)} onDiscard={onClose} onContinue={() => setCloseGuardOpen(false)} />}
    </section>
  </div>;
}
